import { Alert, Dialog, DialogContent, Typography } from '@mui/material';
import { IRecordType } from 'types/recordType';
import { IRecordHeaders } from 'ui-component/records/types';
import { DataFocusViewDialogTitle, DataFocusViewDialogActions } from '../components';
import { DataFocusViewCore, DataFocusViewCoreRef } from 'views/DataFocusView/Core';
import { useRef } from 'react';
import { useConfirmationModalContext } from 'hooks/useConfirmationModal';
import { Warning } from '@mui/icons-material';

export type DataFocusViewDialogDefaultProps = {
    recordId?: number;
    recordType: IRecordType;
    open: boolean;
    onClose: (newRecord?: IRecordHeaders, errorMsg?: string) => void;
    isCreate?: boolean;
    showCloseWarning?: boolean;
};

export const DataFocusViewDialogDefault = ({
    recordType,
    open,
    onClose,
    isCreate,
    recordId: propRecordId,
    showCloseWarning
}: DataFocusViewDialogDefaultProps) => {
    const modal = useConfirmationModalContext();
    const recordTypeId = recordType.id;
    const dataFocusViewCoreRef = useRef<DataFocusViewCoreRef>(null);
    const handleOpenDataFocusView = () => {
        const tenantUrl = localStorage.getItem('tenantPath');
        if (!tenantUrl || !propRecordId || !recordTypeId) return;
        window.open(`${tenantUrl}/data-focus-view/?recordId=${propRecordId}&recordType=${recordTypeId}`, '_blank');
    };

    const handleSubmit = async () => {
        const res = await dataFocusViewCoreRef.current?.handleSubmit();
        if (res) {
            onClose(res as IRecordHeaders);
        }
        console.log('Submit failed', res);
    };

    const handleClickClose = async () => {
        if (!showCloseWarning) {
            onClose();
            return;
        }

        await modal.showConfirmation({
            title: (
                <Typography fontSize="20px" fontWeight={300} color="#000000" sx={{ ml: 1 }}>
                    Are you sure you want to discard your changes?
                </Typography>
            ),
            icon: <Warning color="warning" sx={{ marginLeft: '1rem' }} fontSize="medium" />,
            content: (
                <Alert
                    variant="filled"
                    icon={false}
                    sx={{
                        bgcolor: '#FB9000',
                        color: 'white',
                        borderRadius: '8px !important',
                        height: '32px',
                        fontSize: '14px',
                        width: 'fit-content',
                        padding: '0 16px !important',
                        fontWeight: 500
                    }}
                >
                    This is an irreversible action and will permanently remove your work.
                </Alert>
            ),
            loadingText: 'Deleting',
            forwardButtonText: 'Save changes',
            onForward: handleSubmit,
            showActionButton: true,
            actionButtonText: 'Discard changes',
            onAction: async () => onClose(),
            showCheckbox: false,
            actionType: 'discard_changes',
            sectionTitle: 'record_view_dialog',
            recordType: '',
            hideCloseBtn: true
        });
    };

    return (
        <Dialog open={open} onClose={handleClickClose} maxWidth="lg" sx={{ '& .MuiDialog-paper': { py: 0 } }} fullWidth>
            <DataFocusViewDialogTitle
                onClose={handleClickClose}
                breadcrumbList={[recordType.name, propRecordId ? `${recordType.name}-${propRecordId || ''}` : '']}
                onOpenDataFocusView={handleOpenDataFocusView}
            />
            <DialogContent sx={{ overflowY: 'hidden', height: '70vh', px: 0 }}>
                <DataFocusViewCore
                    ref={dataFocusViewCoreRef}
                    recordId={String(propRecordId)}
                    recordTypeId={recordTypeId}
                    components={{ LINE_ITEMS: true }}
                    removeNotification="success"
                />
            </DialogContent>
            <DataFocusViewDialogActions
                onClose={onClose}
                onSubmit={handleSubmit}
                isSubmitDisabled={!!dataFocusViewCoreRef.current?.methods.formState.errors.length}
            />
        </Dialog>
    );
};
